import { useState, useEffect } from 'react';
import { Settings, BookOpen, Split, Timer, Zap, Navigation, Gauge } from 'lucide-react';
import { CalcTarget, Language, ThemeColor, PaceOrSpeedMode } from '../types';
import { t } from '../utils/i18n';

interface HeaderProps {
  language: Language;
  logCount: number;
  onOpenSettings: () => void;
  onOpenLogbook: () => void;
  onOpenSplits: () => void;
  calcTarget: CalcTarget;
  onChangeCalcTarget: (target: CalcTarget) => void;
  themeColor?: ThemeColor;
  paceOrSpeedMode?: PaceOrSpeedMode;
}

export default function Header({
  language,
  logCount,
  onOpenSettings,
  onOpenLogbook,
  onOpenSplits,
  calcTarget,
  onChangeCalcTarget,
  themeColor = 'violet',
  paceOrSpeedMode = 'pace',
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 15);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isSpeedMode = paceOrSpeedMode === 'speed';

  const targetTabs = [
    {
      id: 'time' as CalcTarget,
      label: t(language, 'uiCalculateTime'),
      shortLabel: t(language, 'targetTime'),
      icon: Timer,
      activeBg: 'bg-orange-700 text-white shadow-xs',
      inactiveClass: 'text-slate-300 hover:text-white hover:bg-white/10',
    },
    {
      id: 'pace' as CalcTarget,
      label: isSpeedMode
        ? (t(language, 'uiCalculateSpeed'))
        : (t(language, 'uiCalculatePace')),
      shortLabel: isSpeedMode
        ? (t(language, 'uiSpeed'))
        : t(language, 'targetPace'),
      icon: isSpeedMode ? Gauge : Zap,
      activeBg: 'bg-blue-700 text-white shadow-xs',
      inactiveClass: 'text-slate-300 hover:text-white hover:bg-white/10',
    },
    {
      id: 'distance' as CalcTarget,
      label: t(language, 'uiCalculateDistance'),
      shortLabel: t(language, 'targetDistance'),
      icon: Navigation,
      activeBg: 'bg-emerald-700 text-white shadow-xs',
      inactiveClass: 'text-slate-300 hover:text-white hover:bg-white/10',
    },
  ];

  return (
    <header
      className={`sticky top-0 z-30 transition-all duration-300 ${
        isScrolled
          ? 'bg-slate-950/90 dark:bg-[#070913]/90 backdrop-blur-xl shadow-lg shadow-black/20 py-2.5 px-4 sm:px-6 border-b border-white/10'
          : 'bg-gradient-to-r from-[#0B0D17] via-[#131827] to-[#1E2538] overflow-hidden rounded-b-[1.75rem] shadow-lg shadow-black/10 pb-4 pt-4 sm:pt-5 px-4 sm:px-6'
      }`}
    >
      <div className="max-w-2xl mx-auto space-y-3">
        {/* Top Header Bar */}
        <div className="flex items-center justify-between">
          {/* Left: Brand & Logo */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 sm:gap-2.5">
              {/* PaceFlow Infinity SVG Logo */}
              <svg
                viewBox="0 0 100 50"
                className={`${isScrolled ? 'w-7 sm:w-8' : 'w-8 sm:w-9'} h-auto drop-shadow-sm shrink-0 transition-all duration-300`}
                fill="none"
                strokeWidth="7"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#8B5CF6" />
                    <stop offset="100%" stopColor="#10B981" />
                  </linearGradient>
                </defs>
                <path
                  d="M 25,10 C 5,10 5,40 25,40 C 45,40 55,10 75,10 C 95,10 95,40 75,40 C 55,40 45,10 25,10 Z"
                  stroke="url(#flowGradient)"
                />
              </svg>

              <h1 className={`${isScrolled ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'} font-extrabold text-white tracking-tight transition-all duration-300`}>
                PaceFlow
              </h1>

              <span className="ml-0.5 px-1.5 py-0.5 rounded-md border border-violet-500/40 bg-violet-500/10 text-xs font-bold text-violet-300 tracking-wider uppercase">
                Pro
              </span>
            </div>

            {!isScrolled && (
              <p className="text-xs sm:text-xs font-medium text-slate-400 mt-0.5 tracking-wide">
                Find Your Pace, Perfect Your Flow.
              </p>
            )}
          </div>

          {/* Right: Controls & Actions */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* When scrolled: Inklappen naar compacte ronde/squircle icoon-knoppen rechtsboven naast het menu */}
            {isScrolled && (
              <div
                role="tablist"
                aria-label={t(language, 'targetTitle')}
                className="flex items-center p-1 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 gap-1 mr-1"
              >
                {targetTabs.map((tab) => {
                  const isActive = calcTarget === tab.id;
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      id={`tab-compact-${tab.id}`}
                      role="tab"
                      type="button"
                      aria-selected={isActive}
                      title={tab.label}
                      onClick={() => onChangeCalcTarget(tab.id)}
                      className={`p-1.5 sm:p-2 rounded-xl flex items-center justify-center transition-all duration-200 active:scale-90 cursor-pointer ${
                        isActive
                          ? `${tab.activeBg} scale-100`
                          : 'text-slate-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 stroke-[2.4]" />
                      <span className="sr-only">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Splits Knop */}
            <button
              id="btn-header-splits"
              type="button"
              onClick={onOpenSplits}
              className={`${
                isScrolled ? 'p-2' : 'p-2 sm:p-2.5'
              } bg-white/10 hover:bg-white/15 text-slate-200 hover:text-white rounded-2xl transition-all active:scale-95 cursor-pointer border border-white/10`}
              aria-label={t(language, 'splitsTooltip')}
              title={t(language, 'splitsTooltip')}
            >
              <Split className="w-4 h-4 sm:w-4.5 sm:h-4.5 stroke-[2]" />
            </button>

            {/* Logboek Knop */}
            <button
              id="btn-header-logbook"
              type="button"
              onClick={onOpenLogbook}
              className={`relative ${
                isScrolled ? 'p-2' : 'p-2 sm:p-2.5'
              } bg-white/10 hover:bg-white/15 text-slate-200 hover:text-white rounded-2xl transition-all active:scale-95 cursor-pointer border border-white/10`}
              aria-label={t(language, 'logbookTooltip')}
              title={t(language, 'logbookTooltip')}
            >
              <BookOpen className="w-4 h-4 sm:w-4.5 sm:h-4.5 stroke-[2]" />
              {logCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center ring-2 ring-slate-900">
                  {logCount > 9 ? '9+' : logCount}
                </span>
              )}
            </button>

            {/* Instellingen Knop */}
            <button
              id="btn-header-settings"
              type="button"
              onClick={onOpenSettings}
              className={`${
                isScrolled ? 'p-2' : 'p-2 sm:p-2.5'
              } bg-white/10 hover:bg-white/15 text-slate-200 hover:text-white rounded-2xl transition-all active:scale-95 cursor-pointer border border-white/10`}
              aria-label={t(language, 'settingsTooltip')}
              title={t(language, 'settingsTooltip')}
            >
              <Settings className="w-4 h-4 sm:w-4.5 sm:h-4.5 stroke-[2]" />
            </button>
          </div>
        </div>

        {/* When NOT scrolled: Full Segmented Control Tab Bar direct ONDER de header */}
        {!isScrolled && (
          <div
            role="tablist"
            aria-label={t(language, 'targetTitle')}
            className="p-1 bg-white/10 backdrop-blur-md rounded-2xl border border-white/10 grid grid-cols-3 gap-1.5 shadow-inner"
          >
            {targetTabs.map((tab) => {
              const isActive = calcTarget === tab.id;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  id={`tab-calc-full-${tab.id}`}
                  role="tab"
                  type="button"
                  aria-selected={isActive}
                  onClick={() => onChangeCalcTarget(tab.id)}
                  className={`flex items-center justify-center rounded-xl font-bold select-none cursor-pointer transition-all duration-200 py-2 sm:py-2.5 px-2 gap-1.5 text-xs sm:text-sm ${
                    isActive
                      ? `${tab.activeBg} scale-100`
                      : 'text-slate-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0 stroke-[2.4]" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.label}</span>
                  <span className="sm:hidden whitespace-nowrap">{tab.shortLabel}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </header>
  );
}

